package org.gymly.ui.admin

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CameraAlt
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminAccessTerminal
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.TooltipIconActionButton
import org.gymly.util.userMessage

@Composable
fun TerminalPhotoCaptureDialog(
    onDismiss: () -> Unit,
    onCaptured: (PickedImagePayload) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var capturing by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var terminals by remember { mutableStateOf<List<AdminAccessTerminal>>(emptyList()) }
    var selectedId by remember { mutableStateOf<String?>(null) }
    var terminalMenu by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        loading = true
        error = null
        try {
            val list = AdminRepo.api.listAccessTerminals().filter { it.enabled }
            terminals = list
            selectedId = list.singleOrNull()?.id ?: list.firstOrNull()?.id
            if (list.isEmpty()) {
                error = "No hay terminales activos. Configura uno en Configuración → Acceso."
            }
        } catch (e: Exception) {
            error = e.userMessage("No se pudieron cargar los terminales.")
        } finally {
            loading = false
        }
    }

    val selected = terminals.firstOrNull { it.id == selectedId }

    AlertDialog(
        onDismissRequest = { if (!capturing) onDismiss() },
        title = { Text("Capturar en terminal Hikvision", fontWeight = FontWeight.Bold) },
        text = {
            Column {
                Text(
                    "Pida a la persona que mire a la cámara del equipo. La captura puede tardar unos segundos.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                )
                Spacer(Modifier.height(12.dp))
                when {
                    loading -> CircularProgressIndicator(color = AdminColors.Primary)
                    error != null -> Text(error!!, color = AdminColors.Error, fontSize = 13.sp)
                    else -> {
                        Text("Terminal", color = AdminColors.TextMuted, fontSize = 12.sp)
                        Spacer(Modifier.height(4.dp))
                        OutlinedButton(
                            onClick = { if (terminals.size > 1) terminalMenu = true },
                            enabled = !capturing && terminals.isNotEmpty(),
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(selected?.name ?: "Seleccionar terminal", color = AdminColors.Text)
                        }
                        DropdownMenu(expanded = terminalMenu, onDismissRequest = { terminalMenu = false }) {
                            terminals.forEach { t ->
                                DropdownMenuItem(
                                    text = { Text(t.name) },
                                    onClick = {
                                        selectedId = t.id
                                        terminalMenu = false
                                    },
                                )
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (capturing) {
                    CircularProgressIndicator(
                        modifier = Modifier.padding(end = 8.dp).size(18.dp),
                        color = AdminColors.Primary,
                        strokeWidth = 2.dp,
                    )
                }
                TooltipIconActionButton(
                    onClick = {
                        val id = selectedId ?: return@TooltipIconActionButton
                        scope.launch {
                            capturing = true
                            error = null
                            try {
                                val photo = AdminRepo.api.captureFaceFromTerminal(id)
                                onCaptured(photo)
                                onDismiss()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo capturar la foto en el terminal.")
                            } finally {
                                capturing = false
                            }
                        }
                    },
                    tooltip = if (capturing) "Capturando…" else "Capturar ahora",
                    icon = Icons.Outlined.CameraAlt,
                    enabled = !loading && !capturing && selectedId != null && error == null,
                    tint = AdminColors.Primary,
                    size = 40.dp,
                )
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !capturing) {
                Text("Cancelar", color = AdminColors.TextMuted)
            }
        },
    )
}
